import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../lib/api';
import { swrLoad } from '../lib/swrCache';
import { useIsAdmin } from '../lib/auth';
import type { Venue } from '../types';
import SectionTabs from '../components/SectionTabs';
import { VENUES_TABS } from '../lib/section-tabs';
import { formatCategoryList, getVenueCategories } from '../lib/venue-categories';
import VenueNeighborhoodEditor from '../components/VenueNeighborhoodEditor';
import VenueFiltersBar from '../components/VenueFiltersBar';
import {
  DEFAULT_VENUE_FILTERS,
  applyVenueFilters,
  type VenueFilterState,
  venueFiltersFromParams,
  venueFiltersToParams,
} from '../lib/venueFilters';
import { useStoredOrigin } from '../lib/useStoredOrigin';

// Fix Leaflet's default marker icons broken by Vite's asset bundling.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Jacksonville / Murray Hill area default center
const DEFAULT_CENTER: [number, number] = [30.3, -81.67];
const DEFAULT_ZOOM = 13;

type MappableVenue = Venue & { lat: number; lng: number };

/** Popup content for a single venue marker, with optional admin neighborhood dropdown. */
function VenuePopupContent({
  venue,
  allNeighborhoods,
  isAdmin,
  onNeighborhoodChange,
}: {
  venue: MappableVenue;
  allNeighborhoods: string[];
  isAdmin: boolean;
  onNeighborhoodChange: (rowKey: string, newNeighborhood: string, manualOverride: boolean) => void;
}) {
  return (
    <>
      <strong>
        <Link to={`/venues/${venue.rowKey}`}>{venue.name}</Link>
      </strong>
      <br />
      {isAdmin ? (
        <div className="mt-1">
          <VenueNeighborhoodEditor
            compact
            venue={venue}
            options={allNeighborhoods}
            onChanged={onNeighborhoodChange}
          />
          {getVenueCategories(venue).length > 0 && (
            <div className="text-muted mh-hood-editor__meta">
              {formatCategoryList(getVenueCategories(venue))}
            </div>
          )}
        </div>
      ) : (
        <span className="text-muted mh-hood-editor__meta">
          {venue.partitionKey}
          {getVenueCategories(venue).length > 0 && <> &middot; {formatCategoryList(getVenueCategories(venue))}</>}
        </span>
      )}
      {venue.address && (
        <>
          <br />
          <span style={{ fontSize: '0.85em' }}>{venue.address}</span>
        </>
      )}
      {venue.website && (
        <>
          <br />
          <a
            href={venue.website}
            target="_blank"
            rel="noreferrer noopener"
            style={{ fontSize: '0.85em' }}
          >
            Website ↗
          </a>
        </>
      )}
    </>
  );
}

/** Imperative side-effect: re-center & reasonably zoom the map when the
 *  origin changes. Mounted as a child of <MapContainer> so it can call useMap. */
function MapOriginEffect({ origin, radiusMiles }: { origin?: { lat: number; lng: number }; radiusMiles?: number }) {
  const map = useMap();
  useEffect(() => {
    if (!origin) return;
    const zoom = !radiusMiles || radiusMiles >= 5 ? 12 : radiusMiles >= 2 ? 13 : 14;
    map.flyTo([origin.lat, origin.lng], zoom, { duration: 0.6 });
  }, [origin?.lat, origin?.lng, radiusMiles, map]);
  return null;
}

export default function VenueMapPage() {
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = useIsAdmin();
  const { origin, maxDistanceMiles, setOriginState } = useStoredOrigin();

  const [filters, setFilters] = useState<VenueFilterState>(() =>
    venueFiltersFromParams(searchParams, DEFAULT_VENUE_FILTERS),
  );

  // Origin/distance live in the shared store; merge in only at filter time
  // so we don't need bridge effects (they previously caused a render loop
  // with the geocode fallback inside `useStoredOrigin`).
  const effectiveFilters = useMemo<VenueFilterState>(
    () => ({ ...filters, origin, maxDistanceMiles }),
    [filters, origin, maxDistanceMiles],
  );

  // Sync filter state to URL.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const params = venueFiltersToParams(filters, DEFAULT_VENUE_FILTERS);
    for (const [k, v] of Object.entries(params)) {
      if (v == null) next.delete(k);
      else next.set(k, v);
    }
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    void swrLoad<Venue[]>({
      key: 'venues:public',
      fetcher: () => api.get<Venue[]>('/api/venues/public'),
      onData: (data) => setVenues(data),
      onError: () => setVenues([]),
      isCancelled: () => cancelled,
    });
    return () => { cancelled = true; };
  }, []);

  // Venues that have coordinates — required to place a map marker.
  const mappable = useMemo(
    () => (venues ?? []).filter((v): v is MappableVenue => v.lat != null && v.lng != null),
    [venues]
  );

  const allNeighborhoods = useMemo(
    () => Array.from(new Set(mappable.map((v) => v.partitionKey))).sort(),
    [mappable]
  );

  const visible = useMemo<MappableVenue[]>(
    () => applyVenueFilters(mappable, effectiveFilters) as MappableVenue[],
    [mappable, effectiveFilters],
  );

  const totalCount = venues?.length ?? 0;
  const mappableCount = mappable.length;
  const visibleCount = visible.length;
  const radiusMeters =
    origin && typeof maxDistanceMiles === 'number' && maxDistanceMiles > 0
      ? maxDistanceMiles * 1609.344
      : null;

  const handleNeighborhoodChange = (rowKey: string, newNeighborhood: string, manualOverride: boolean) => {
    setVenues((prev) =>
      prev ? prev.map((v) => v.rowKey === rowKey ? { ...v, partitionKey: newNeighborhood, manualNeighborhoodOverride: manualOverride } : v) : prev
    );
  };

  return (
    <SectionTabs ariaLabel="Venues sections" items={VENUES_TABS}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h1 className="mb-0">Venue map</h1>
      </div>

      <VenueFiltersBar
        venues={venues ?? []}
        filters={filters}
        setFilters={(updater) => setFilters(updater)}
        mode="public"
        showSort={false}
        originOverride={origin}
        maxDistanceMilesOverride={maxDistanceMiles}
        onOriginChange={setOriginState}
        idPrefix="mh-venuemap"
      />

      {/* Status line */}
      <p className="small text-muted mb-2">
        {venues === null
          ? 'Loading venues…'
          : `Showing ${visibleCount} of ${mappableCount} mapped venue${mappableCount !== 1 ? 's' : ''}${totalCount > mappableCount ? ` (${totalCount - mappableCount} venue${totalCount - mappableCount !== 1 ? 's' : ''} have no coordinates yet)` : ''}.`
        }
      </p>

      {/* Map */}
      <div className="rounded border overflow-hidden mh-map-container">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          className="mh-map-fill"
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapOriginEffect origin={origin} radiusMiles={maxDistanceMiles} />
          {origin && radiusMeters && (
            <Circle
              center={[origin.lat, origin.lng]}
              radius={radiusMeters}
              pathOptions={{ color: '#0d6efd', weight: 1, fillOpacity: 0.08 }}
            />
          )}
          {visible.map((v) => (
            <Marker key={v.rowKey} position={[v.lat, v.lng]}>
              <Popup minWidth={isAdmin ? 200 : undefined}>
                <VenuePopupContent
                  venue={v}
                  allNeighborhoods={allNeighborhoods}
                  isAdmin={isAdmin}
                  onNeighborhoodChange={handleNeighborhoodChange}
                />
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </SectionTabs>
  );
}
